import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { ArrowLeft, Scales } from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import { IS_FARMERSPULSE, SITE_META } from "@/lib/site";

/**
 * Public per-page view for /legal/:slug — renders the CMS-authored HTML
 * body. Injects per-page OG/Twitter meta tags for social shares.
 */
export default function LegalPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const legalHome = IS_FARMERSPULSE ? "/legal" : "/farmers/legal";
  const shareUrl = useMemo(
    () => (typeof window !== "undefined" ? window.location.href : ""),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setPage(null); setNotFound(false);
    http.get(`/cms/pages/by-slug/legal/${slug}`)
      .then(({ data }) => { if (!cancelled) setPage(data.page); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    return () => { cancelled = true; };
  }, [slug]);

  // Per-page meta (title + OG)
  useEffect(() => {
    if (!page) return;
    const prevTitle = document.title;
    document.title = `${page.title} · ${SITE_META.brand}`;
    const metas = [
      { property: "og:type",        content: "article" },
      { property: "og:title",       content: page.title },
      { property: "og:description", content: page.excerpt || `${SITE_META.brand} legal notice` },
      { property: "og:url",         content: shareUrl },
      { name: "twitter:card",       content: "summary_large_image" },
      { name: "twitter:title",      content: page.title },
      { name: "twitter:description", content: page.excerpt || `${SITE_META.brand} legal notice` },
      { name: "description",        content: page.excerpt || `${SITE_META.brand} legal notice` },
    ];
    const created = [];
    for (const m of metas) {
      const selector = m.property
        ? `meta[property="${m.property}"]`
        : `meta[name="${m.name}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        if (m.property) el.setAttribute("property", m.property);
        if (m.name)     el.setAttribute("name", m.name);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", m.content);
    }
    return () => { document.title = prevTitle; created.forEach((el) => el.remove()); };
  }, [page, shareUrl]);

  if (notFound) {
    return (
      <main className="min-h-screen pt-24 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#241209]">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="font-serif-display text-3xl">Not found.</div>
          <Link to={legalHome} className="mt-4 inline-block font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] underline">
            ← Back to Legal
          </Link>
        </div>
      </main>
    );
  }
  if (!page) {
    return <main className="min-h-screen pt-24 pb-24"><div className="max-w-3xl mx-auto px-5 md:px-8 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#8E8B7F]">Loading…</div></main>;
  }

  // Plain-text version for TTS / translate — strip HTML tags cheaply.
  const plainText = (page.body_html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

  return (
    <main
      data-testid="legal-page"
      className="min-h-screen pt-24 md:pt-28 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#241209]"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <Link
          to={legalHome}
          data-testid="legal-page-back"
          className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]"
        >
          <ArrowLeft size={11} weight="bold" /> All legal notices
        </Link>

        <div className="mt-6 font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
          <Scales size={12} weight="fill" /> {SITE_META.brand} · Legal
        </div>
        <h1 className="mt-3 font-serif-display text-4xl md:text-6xl leading-[0.98] tracking-tight text-[#151312]">
          {page.title}
        </h1>
        {page.subtitle && (
          <div className="mt-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
            {page.subtitle}
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2 items-center">
          <ListenTranslateBar text={plainText} testidPrefix="legal-page-tools" />
          <ShareButton
            title={page.title}
            text={page.excerpt || SITE_META.brand}
            hashtags={["FarmersPulse"]}
            variant="chip"
            testid="legal-page-share"
          />
        </div>

        <article
          data-testid="legal-page-body"
          className="mt-10 max-w-none text-[#241209] text-lg leading-relaxed [&_h1]:font-serif-display [&_h1]:text-3xl [&_h1]:md:text-4xl [&_h1]:text-[#151312] [&_h1]:mt-10 [&_h1]:mb-3 [&_h2]:font-serif-display [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:tracking-tight [&_h2]:text-[#151312] [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-serif-display [&_h3]:text-xl [&_h3]:text-[#151312] [&_h3]:mt-6 [&_h3]:mb-2 [&_strong]:text-[#151312] [&_p]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ul]:space-y-1 [&_a]:text-[color:var(--np-brand,#B85F2A)] [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: page.body_html || "" }}
        />
      </div>
    </main>
  );
}
